import utils from '../../utils'
import {
	nodeTypeToImage
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/defaults/node-types";

const defaults = {
	container: false,
	readonly: false,
	nodeTypes: [],
	dragThresholdPx: 10,
	previewOffsetY: 14,
}

class TouchDragAddNodes {
	constructor(cy, params) {
		this.cy = cy
		this._options = Object.assign({}, defaults, params)
		this._activeDragSession = null
		this._suppressClickUntilTs = 0
		this._bodyOverflowOriginal = ""
		this._listeners = {}

		if(this._options.readonly || this._options.nodeTypes.length < 1){
			return
		}

		this._initContainer()
		if(!this._shapePanel){
			return
		}
		this._initEvents()
	}

	_initContainer() {
		let {_options} = this
		if (_options.container) {
			if (typeof _options.container === 'string') {
				this._shapePanel = utils.query(_options.container)[0]
			} else if (utils.isNode(_options.container)) {
				this._shapePanel = _options.container
			}
		}
	}

	_initEvents() {
		this._listeners.touchstart = event => this._handleTouchStart(event)
		this._listeners.touchmove = event => this._handleTouchMove(event)
		this._listeners.touchend = event => this._handleTouchEnd(event)
		this._listeners.touchcancel = event => this._handleTouchCancel(event)
		this._listeners.clickCapture = event => this._handleClickCapture(event)

		this._shapePanel.addEventListener('touchstart', this._listeners.touchstart, {passive: false})
		window.addEventListener('touchmove', this._listeners.touchmove, {passive: false})
		window.addEventListener('touchend', this._listeners.touchend, {passive: false})
		window.addEventListener('touchcancel', this._listeners.touchcancel, {passive: false})
		this._shapePanel.addEventListener('click', this._listeners.clickCapture, true)
	}

	_handleTouchStart(event) {
		let shapeTarget = this._findShapeTarget(event.target)
		if(!shapeTarget){
			return
		}

		let touch = event.changedTouches?.[0]
		if(!touch){
			return
		}

		let shapeId = this._getShapeId(shapeTarget)
		let shape = this._options.nodeTypes.find(item => item._id === shapeId)
		if(!shape){
			return
		}

		this._activeDragSession = {
			touchId: touch.identifier,
			shape,
			startX: touch.clientX,
			startY: touch.clientY,
			lastX: touch.clientX,
			lastY: touch.clientY,
			isDragging: false,
			previewEl: null,
		}
	}

	_handleTouchMove(event) {
		let touch = this._findSessionTouch(event.changedTouches)
		if(!touch || !this._activeDragSession){
			return
		}

		this._activeDragSession.lastX = touch.clientX
		this._activeDragSession.lastY = touch.clientY

		if(!this._activeDragSession.isDragging){
			let distance = this._calculateDistance(
				this._activeDragSession.startX,
				this._activeDragSession.startY,
				touch.clientX,
				touch.clientY
			)
			if(distance < this._options.dragThresholdPx){
				return
			}

			this._startDragging(event)
		}

		event.preventDefault()
		this._updatePreviewPosition(touch.clientX, touch.clientY)
	}

	_handleTouchEnd(event) {
		let touch = this._findSessionTouch(event.changedTouches)
		if(!touch || !this._activeDragSession){
			return
		}

		let shouldSuppressClick = this._activeDragSession.isDragging
		if(shouldSuppressClick){
			event.preventDefault()
			this._suppressClickUntilTs = Date.now() + 700
			this._dropActiveDrag(touch.clientX, touch.clientY)
		}

		this._cleanupActiveDragSession()
	}

	_handleTouchCancel() {
		this._cleanupActiveDragSession()
	}

	_handleClickCapture(event) {
		if(Date.now() >= this._suppressClickUntilTs){
			return
		}
		if(!this._findShapeTarget(event.target)){
			return
		}

		event.preventDefault()
		event.stopPropagation()
	}

	_startDragging(event) {
		if(!this._activeDragSession){
			return
		}

		this._activeDragSession.isDragging = true
		this._activeDragSession.previewEl = this._createPreviewElement(this._activeDragSession.shape)
		this._toggleBodyScrollLock(true)
		this.cy.trigger('cyeditor.addnodestart', {
			sourceEvent: event,
			shape: this._activeDragSession.shape,
			triggerType: 'touchdrag',
		})
	}

	_dropActiveDrag(clientX, clientY) {
		if(!this._activeDragSession?.shape){
			return
		}

		let container = this.cy.container()
		let bounds = container?.getBoundingClientRect?.()
		if(!bounds || !this._isPointInsideRect(clientX, clientY, bounds)){
			return
		}

		let position = {
			x: clientX - bounds.left,
			y: clientY - bounds.top,
		}
		this.cy.trigger('cyeditor.addnodeplaceattempt', {
			shape: this._activeDragSession.shape,
			triggerType: 'touchdrag',
		})
		this.cy.trigger('cyeditor.addnode', this._buildNodeForShape(this._activeDragSession.shape, position))
	}

	_buildNodeForShape(nodeData, position) {
		let {type = null, width, height, bg, opacity, border, name = '', points, buildIn = false, src} = nodeData
		let {data = {}, ...scratchData} = nodeData
		let dataLocal = {id: '' + new Date().getTime(), type, name, label: name, width, height, ...data}
		if(bg){
			dataLocal.bg = bg
		}
		if(opacity){
			dataLocal.opacity = opacity
		}
		if(border){
			dataLocal.border = border
		}
		if (!buildIn) {
			dataLocal.image = src
		}

		let node = {
			group: 'nodes',
			data: dataLocal,
			scratch: {
				...scratchData,
				addedFromDragAddNodes: true,
			},
			position,
		}
		if(points){
			node.data.points = points
		}
		return node
	}

	_createPreviewElement(shape) {
		let previewEl = document.createElement('div')
		let img = document.createElement('img')
		let label = document.createElement('div')

		previewEl.style.position = 'fixed'
		previewEl.style.left = '0'
		previewEl.style.top = '0'
		previewEl.style.zIndex = '100000'
		previewEl.style.pointerEvents = 'none'
		previewEl.style.transform = 'translate(-50%, -50%)'
		previewEl.style.background = 'rgba(28, 36, 52, 0.92)'
		previewEl.style.borderRadius = '10px'
		previewEl.style.padding = '10px 12px'
		previewEl.style.boxShadow = '0 10px 24px rgba(16, 24, 40, 0.28)'
		previewEl.style.display = 'flex'
		previewEl.style.flexDirection = 'column'
		previewEl.style.alignItems = 'center'
		previewEl.style.gap = '6px'
		previewEl.style.opacity = '0.92'

		img.src = shape?.src || nodeTypeToImage[shape?.type]
		img.alt = shape?.label || shape?.name || ''
		img.style.width = '56px'
		img.style.height = '56px'
		img.style.objectFit = 'contain'

		label.textContent = String(shape?.label || shape?.name || '').replaceAll('<br/>', ' ')
		label.style.color = '#fff'
		label.style.fontSize = '12px'
		label.style.fontWeight = '600'
		label.style.textAlign = 'center'
		label.style.maxWidth = '110px'
		label.style.lineHeight = '1.25'

		previewEl.appendChild(img)
		if(label.textContent){
			previewEl.appendChild(label)
		}
		document.body.appendChild(previewEl)
		this._updatePreviewPosition(this._activeDragSession.lastX, this._activeDragSession.lastY)
		return previewEl
	}

	_updatePreviewPosition(clientX, clientY) {
		let previewEl = this._activeDragSession?.previewEl
		if(!previewEl){
			return
		}

		previewEl.style.left = `${clientX}px`
		previewEl.style.top = `${clientY - this._options.previewOffsetY}px`
	}

	_cleanupActiveDragSession() {
		if(this._activeDragSession?.previewEl){
			this._activeDragSession.previewEl.remove()
		}
		this._toggleBodyScrollLock(false)
		this._activeDragSession = null
	}

	_toggleBodyScrollLock(isLocked) {
		if(typeof document === 'undefined'){
			return
		}

		if(isLocked){
			this._bodyOverflowOriginal = document.body.style.overflow
			document.body.style.overflow = 'hidden'
			return
		}

		document.body.style.overflow = this._bodyOverflowOriginal || ''
		this._bodyOverflowOriginal = ''
	}

	_findSessionTouch(touchList) {
		if(!this._activeDragSession || !touchList){
			return null
		}
		return Array.from(touchList).find(item => item.identifier === this._activeDragSession.touchId) || null
	}

	_findShapeTarget(target) {
		return target?.closest?.('[data-shape-id]') || target?.closest?.('.shape-item') || null
	}

	_getShapeId(shapeTarget) {
		return shapeTarget?.getAttribute?.('data-shape-id') || shapeTarget?.getAttribute?.('data-id') || ''
	}

	_calculateDistance(startX, startY, nextX, nextY) {
		let deltaX = nextX - startX
		let deltaY = nextY - startY
		return Math.sqrt(deltaX * deltaX + deltaY * deltaY)
	}

	_isPointInsideRect(clientX, clientY, rect) {
		return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
	}
}

export default (cytoscape) => {
	if (!cytoscape) {
		return
	}

	cytoscape('core', 'touchDragAddNodes', function (params) {
		return new TouchDragAddNodes(this, params)
	})
}
